import { Component, ElementRef, HostListener, Input, OnInit, ViewChild, inject } from '@angular/core';
import { LocalizationInfo } from '@sb/library/data-models';
import { LocalizationService, SBTime } from '@sb/library/localization';
import { MbscDatepicker, MbscDatepickerChangeEvent, MbscDatepickerOpenEvent } from '@mobiscroll/angular';
import { FormFieldInputBaseComponent } from '../form-field-input-base.component';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
import { FormFieldInputContainerComponent } from '../../container/form-field-container.component';

@Component({
    selector: 'lib-input-time',
    templateUrl: './form-field-time.component.html',
    styleUrls: ['./form-field-time.component.scss']
})
export class FormFieldTimeComponent extends FormFieldInputBaseComponent<SBTime | null> implements OnInit {

    @Input() minuteStep: number = this.isMobileOrTablet ? 1 : 5;
    @Input() minimumHour: number = 0;
    @Input() maximumHour: number = 23;
    @Input() canSetMinutes: boolean = true;

    @ViewChild('menuTrigger', { static: false }) menu?: MatMenuTrigger;

    override readonly hasPrefixIconButton: boolean = true;

    private _timeSetFromInitialTime = true;
    private _timeSetManually = true;
    private _time: SBTime = SBTime.Now();
    private _timeFormat?: string;
    private _circularityArray: boolean[] = [true, true, true];
    private _LocalizationService: LocalizationService;


    constructor() {
        super();
        this.prefixIcon = this.Icon.Common.Time;
        this._LocalizationService = inject(LocalizationService);
    }

    override ngOnInit() {
        super.ngOnInit();
        this._timeFormat = this._LocalizationService.ShortTimePattern;
        if (this._LocalizationService.Is12Hour) {
            if (this._LocalizationService.AMDesignator.toLocaleLowerCase() === this._LocalizationService.AMDesignator) {
                this._timeFormat = this._timeFormat.replace(/t+/, 'a');
            } else {
                this._timeFormat = this._timeFormat.replace(/t+/, 'A');
            }
            this._circularityArray[this._time.meridianPlacement] = false;
        }

        if (!this.canSetMinutes) {
            this._timeFormat = this._timeFormat.replace(/m+/, '');
            this._circularityArray.splice(this._time.minutePlacement, 1);
        }
    }

    override writeValue(value: SBTime | null): void {
        if (value) {
            this.ApplyInitialTime(value.ToISO());
        }
    }

    private ApplyInitialTime(dateTimeISO: string) {
        let sbTime: SBTime;
        try {
            sbTime = SBTime.FromISO(dateTimeISO)
        } catch {
            sbTime = SBTime.Now();
        }
        this._time = sbTime;
        this.UpdateTimePickerInput();
    }

    override ObservePristine(): void {
        // This is done below in ChangeEvent instead
    }

    StopEditing() {
        this.UpdateTimePickerInput();
        this.formControl.isDirty = true;
    }

    TimePickerUsed() {
        this._timeSetManually = false;
        this.formControl.isDirty = true;
    }


    OnClick(event: MouseEvent): void {
        if (this.isDisabled) {
            event.preventDefault();
        }
    }

    CloseMenu(): void {
        this.menu?.closeMenu();
        this.UpdateTimePickerInput();
    }

    OpenMenu(anchor: FormFieldInputContainerComponent): void {
        anchor.panelContainer.el.nativeElement.click();
    }

    private UpdateTimePickerElement(time: string) {
        try {
            this._time = SBTime.Parse(time);
            if (!this.canSetMinutes) {
                this._time.minute = 0;
            }
        } catch {}
    }

    private UpdateTimePickerInput() {
        if (!this.canSetMinutes) {
            this._time.minute = 0;
        }
        this.viewControl.setValue(this._time.Render());
        this.SetCleanValue();
    }

    private SetCleanValue() {
        if (!this.formControl?.isLoading) {
            setTimeout(() => this.formControl.setValue(this._time, { emitModelToViewChange: false }));
        }
    }

    get time(): string {
        return this._time.ToISO();
    }

    // TODO: Revisit this and try to get Mobiscroll to behave
    set time(value: Date) {
        if (!this._timeSetManually) {
            this._timeSetFromInitialTime = false;
            if (typeof value !== 'string') {
                this._time = SBTime.FromJSDate(value);
            }
            this.UpdateTimePickerInput();
        }
    }

    get timeFormat(): string | undefined {
        return this._timeFormat;
    }

    get amDesignator(): string {
        return this._LocalizationService.AMDesignator;
    }

    get pmDesignator(): string {
        return this._LocalizationService.PMDesignator;
    }

    get circularityArray(): boolean[] {
        return this._circularityArray;
    }

    get localeInfo(): LocalizationInfo {
        return this._LocalizationService.LocalizationInfo;
    }

    @HostListener('keyup', ['$event'])
    onKeyDown(event: KeyboardEvent) {
        this._timeSetFromInitialTime = false;
        this._timeSetManually = true;
        setTimeout(() => {
            this.UpdateTimePickerElement(this.viewControl.value ?? '');
            this.SetCleanValue();
        });
    }
}
